// HistoryConvertingCoinsIntoCash.jsx
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { CartOrders } from "../../services/services";
import { useResponsivePageSize } from "../../hook/useResponsivePageSize";
import ResponsiveDataList from "../../components/ResponsiveDataList/ResponsiveDataList";
import PersianDate from "../../components/PersianDate/PersianDate";

const stateFormatter = (value) => {
  if (value === 1) {
    return (
      <div
        style={{
          color: "#fff",
          background: "#5ca03cad",
          display: "flex",
          justifyContent: "center",
          width: "70%",
          borderRadius: "10px",
          padding: "0 4px",
        }}
      >
        واریز شده
      </div>
    );
  }
  if (value === 2) {
    return (
      <div
        style={{
          color: "#fff",
          background: "#dd2c2cad",
          display: "flex",
          justifyContent: "center",
          width: "70%",
          borderRadius: "10px",
          padding: "0 4px",
        }}
      >
        واریز نشده
      </div>
    );
  }
  return (
    <div
      style={{
        color: "#fff",
        background: "#e7a151",
        display: "flex",
        justifyContent: "center",
        width: "70%",
        borderRadius: "10px",
        padding: "0 4px",
      }}
    >
      در حال انتظار
    </div>
  );
};

const HistoryConvertingCoinsIntoCash = ({ Name, isMobile }) => {
  // ثابت طبق کد شما
  const productCategoryType = 1;

  const defaultPageSize = useResponsivePageSize({ defaultSmall: 10 });
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const formatAddress = (addr) => {
    if (!addr) return "";
    if (typeof addr === "string") return addr;

    const {
      title,
      provinceName,
      cityName,
      addressDetails,
      postalCode,
      number,
      unit,
    } = addr;

    const parts = [
      title,
      [provinceName, cityName].filter(Boolean).join("، "),
      addressDetails,
      number ? `پلاک: ${number}` : null,
      unit ? `واحد: ${unit}` : null,
      postalCode && postalCode.trim() ? `کدپستی: ${postalCode}` : null,
    ].filter(Boolean);

    return parts.join(" | ");
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["CartOrders", Name, productCategoryType, pageNumber, pageSize],
    queryFn: () =>
      CartOrders({
        Name,
        ProductCategoryType: productCategoryType,
        pageSize,
        pageNumber: pageNumber || 1,
      }),
    enabled: !!Name,
    select: (res) => res,
  });

  const rows = useMemo(() => {
    if (!data) return [];
    return data?.items ?? data?.data ?? data?.result ?? [];
  }, [data]);

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return (
      data?.totalCount ?? data?.totalRecords ?? data?.count ?? rows.length ?? 0
    );
  }, [data, rows.length]);

  const columns = useMemo(() => {
    const cols = [
      {
        header: "تاریخ ",
        body: (r) => <PersianDate date={r?.creationTime} />,
        style: { textAlign: "right" },
      },
      { field: "ibanCode", header: "کد شبا", style: { textAlign: "right" } },
      {
        field: "totalAmount",
        header: "تعداد سکه تبدیل شده",
        style: { textAlign: "right" },
      },
      {
        header: "سکه به ریال",
        body: (r) => <span>{Number(r?.totalAmount ?? 0) * 10000}</span>,
        style: { textAlign: "right" },
      },
      {
        header: "موبایل",
        body: (r) => <span>{r?.userInfo?.phoneNumber ?? "-"}</span>,
        style: { textAlign: "right" },
      },
      {
        header: "وضعیت واریز ",
        body: (r) => <span>{stateFormatter(r?.status)}</span>,
        style: { textAlign: "right" },
      },
    ];

    return cols;
  }, [formatAddress]);

  const itemTemplate = (item) => {
    const findColValue = (col) => {
      if (col.body) return col.body(item);
      if (col.field) return item?.[col.field] ?? "-";
      return "-";
    };

    const cardFields = [
      columns[0],
      columns[1],
      columns[2],
      columns[4],
      columns[6],
      columns[7],
    ].filter(Boolean);

    return (
      <div className="d-flex flex-column gap-2">
        {cardFields.map((col, idx) => (
          <div key={idx} className="d-flex justify-content-between gap-2">
            <strong style={{ minWidth: 110 }}>{col.header}:</strong>
            <span style={{ textAlign: "left" }}>{findColValue(col)}</span>
          </div>
        ))}
        <div className="d-flex justify-content-between gap-2">
          <strong style={{ minWidth: 110 }}>آدرس:</strong>
          <span style={{ textAlign: "left" }}>
            {formatAddress(item?.address) || "-"}
          </span>
        </div>
      </div>
    );
  };

  if (!Name) return null;

  if (isLoading) {
    return <div className="p-3">در حال دریافت اطلاعات...</div>;
  }

  if (isError) {
    return <div className="p-3 text-danger">خطا در دریافت اطلاعات</div>;
  }

  return (
    <div className="card mt-4" style={isMobile ? {} : { height: "650px" }}>
      <h5> تاریخچه تبدیل سکه به پول نقد</h5>
      {rows?.length > 0 ? (
        <div className="background-dataTable">
          <ResponsiveDataList
            data={rows}
            columns={columns}
            itemTemplate={itemTemplate}
            dataViewHeader={<></>}
            className="farsi-monospace-digit"
            rows={pageSize}
            pageNumber={pageNumber}
            totalCount={totalCount}
            onPageChange={({ pageNumber: p, pageSize: s }) => {
              setPageNumber(p);
              setPageSize(s);
            }}
          />
        </div>
      ) : (
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "rgb(100, 100, 100)",
          }}
        >
          <h5>موردی یافت نشد !</h5>
        </div>
      )}
    </div>
  );
};

export default HistoryConvertingCoinsIntoCash;
